import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/seo/SEOHead';
import { FileText, Globe, Shield, ArrowRight, CheckCircle, Lock, Clock } from 'lucide-react';

const services = [
  {
    title: 'Record Suspension',
    description: 'Administrative preparation for Record Suspension applications under the Criminal Records Act. Eligibility review conducted prior to any filing.',
    icon: FileText,
    path: '/record-suspensions',
  },
  {
    title: 'U.S. Entry Waivers',
    description: 'Document coordination for U.S. Entry Waiver applications to U.S. Customs and Border Protection for individuals with inadmissibility status.',
    icon: Globe,
    path: '/us-entry-waivers',
  },
  {
    title: 'International Filings',
    description: 'Criminal record authentication and document preparation for international requirements, including UAE/Dubai documentation services.',
    icon: Shield,
    path: '/international-filings',
  },
];

const processSteps = [
  {
    number: 1,
    title: 'Eligibility Check',
    description: 'Answer a few quick questions to determine if you may qualify. We only request contact information when you choose to proceed.',
  },
  {
    number: 2,
    title: 'Document Review',
    description: 'We confirm eligibility requirements and outline the documents needed such as RCMP records or court documentation.',
  },
  {
    number: 3,
    title: 'Application Preparation & Submission',
    description: 'The application package is prepared and submitted following the appropriate government procedures.',
  },
];

const trustPoints = [
  'Federally Incorporated in Canada',
  'Operating Nationally from Toronto, Ontario',
  'No filing initiated without review',
];

const scrollToProcess = () => {
  const element = document.getElementById('how-process-works');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

export default function Home() {
  return (
    <>
      <SEOHead page="home" />
      
      <div data-testid="home-page">
        {/* Hero Section */}
        <section className="bg-[hsl(215,50%,23%)] py-14 md:py-20" data-testid="hero-section">
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Canadian Document Services
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-2">
              Government Application Processing
            </p>
            <p className="text-white/70 text-base md:text-lg mb-8">
              Record Suspension &bull; U.S. Entry Waivers &bull; International Filings
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/eligibility">
                <Button 
                  className="bg-white text-[hsl(215,50%,23%)] hover:bg-gray-100 font-semibold px-8 py-3 rounded-sm"
                  data-testid="hero-cta-btn"
                >
                  Check Eligibility
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-3 rounded-sm"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Administrative Processing Notice - Trust Strip */}
      <section className="bg-[hsl(45,30%,96%)] border-y border-[hsl(45,20%,85%)] py-6" data-testid="admin-notice">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-4">
            <h2 className="text-[hsl(215,50%,23%)] font-semibold text-base mb-2">Administrative Processing Notice</h2>
            <p className="text-gray-600 text-sm max-w-3xl mx-auto">
              Canadian Document Services Inc. provides administrative assistance with application preparation and submission. We are not a government agency.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Shield className="h-4 w-4 text-[hsl(215,50%,23%)]" />
              <span>No SIN or criminal record details collected during eligibility check</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Lock className="h-4 w-4 text-[hsl(215,50%,23%)]" />
              <span>Secure encrypted transmission (HTTPS)</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="h-4 w-4 text-[hsl(215,50%,23%)]" />
              <span>Eligibility check takes approximately 1 minute</span>
            </div>
          </div>
          
          <div className="text-center">
            <button 
              onClick={scrollToProcess}
              className="text-[hsl(210,80%,40%)] text-sm hover:text-[hsl(210,80%,30%)] hover:underline inline-flex items-center gap-1"
              data-testid="read-process-link"
            >
              Read how the process works
              <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-[hsl(210,20%,96%)] py-6 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {trustPoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle className="h-4 w-4 text-[hsl(215,50%,23%)]" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="gov-section" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="gov-section-title">Our Services</h2>
            <p className="gov-subtitle max-w-2xl mx-auto">
              Administrative document preparation services for individuals requiring structured guidance with government applications.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.path}
                className="gov-card group"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <service.icon className="h-6 w-6 text-[hsl(215,50%,23%)]" />
                  <h3 className="text-lg font-bold text-[hsl(215,50%,23%)]">{service.title}</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                <span className="inline-flex items-center text-[hsl(210,80%,40%)] text-sm font-medium group-hover:text-[hsl(210,80%,30%)]">
                  Learn More
                  <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How the Process Works Section */}
      <section id="how-process-works" className="gov-section-alt" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="gov-section-title">How the Process Works</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className="bg-white border border-gray-200 p-6"
                data-testid={`process-step-${step.number}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[hsl(215,50%,23%)] text-white flex items-center justify-center font-bold text-lg shrink-0">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-8">
            Results vary depending on individual circumstances and eligibility requirements.
          </p>
        </div>
      </section>

      {/* Privacy & Data Handling Section */}
      <section className="gov-section bg-white" data-testid="privacy-section">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="gov-section-title">Privacy & Data Handling</h2>
          </div>
          
          <div className="bg-[hsl(210,20%,97%)] border border-gray-200 p-6 md:p-8">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(215,50%,23%)] mt-0.5 shrink-0" />
                <span className="text-gray-700">Minimal information collected – name and phone only during eligibility check</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(215,50%,23%)] mt-0.5 shrink-0" />
                <span className="text-gray-700">Secure encrypted communication using HTTPS</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[hsl(215,50%,23%)] mt-0.5 shrink-0" />
                <span className="text-gray-700">Automated spam protection to prevent fraudulent submissions</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="gov-section-alt" data-testid="expectations-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="gov-section-title">What to Expect</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Record Suspensions */}
            <div className="bg-white border border-gray-200 p-6 md:p-8">
              <h3 className="font-bold text-[hsl(215,50%,23%)] text-xl mb-4">Record Suspension</h3>
              <div className="space-y-3 text-gray-700">
                <p><span className="font-medium">Typical timeline:</span> approximately 6–9 months</p>
                <p><span className="font-medium">Key requirement:</span> completion of sentencing and waiting period.</p>
              </div>
            </div>
            
            {/* U.S. Entry Waivers */}
            <div className="bg-white border border-gray-200 p-6 md:p-8">
              <h3 className="font-bold text-[hsl(215,50%,23%)] text-xl mb-4">U.S. Entry Waivers</h3>
              <div className="space-y-3 text-gray-700">
                <p><span className="font-medium">Typical timeline:</span> approximately 12–18 months</p>
                <p><span className="font-medium">Key requirement:</span> completion of waiver forms and supporting documentation.</p>
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-500 text-sm mt-8">
            Processing timelines vary depending on individual circumstances.
          </p>
        </div>
      </section>

      {/* Primary CTA Section */}
      <section className="gov-section bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Link to="/eligibility">
            <Button 
              className="gov-btn-primary text-lg px-10 py-4"
              data-testid="bottom-cta-btn"
            >
              Check Eligibility
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-gray-500 text-sm mt-4">
            Fast eligibility assessment. No obligation.
          </p>
        </div>
      </section>

      {/* Operational Structure */}
      <section className="gov-section bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">Operational Structure and Regulatory Alignment</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Structural Legitimacy */}
            <div className="gov-panel">
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-4 text-lg">Structural Legitimacy</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>Canadian Document Services Inc.</li>
                <li>Federally Incorporated – Canada</li>
                <li>Operating Nationally</li>
                <li>Office Location: Toronto, Ontario</li>
              </ul>
            </div>
            
            {/* Operational Controls */}
            <div className="gov-panel">
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-4 text-lg">Operational Controls</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>All files undergo structured intake verification</li>
                <li>Court documentation retrieved and cross verified</li>
                <li>RCMP record confirmation completed</li>
                <li>Eligibility verified prior to submission</li>
              </ul>
            </div>
            
            {/* Regulatory Alignment */}
            <div className="gov-panel">
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-4 text-lg">Regulatory Alignment</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>Aligned with Criminal Records Act</li>
                <li>Parole Board of Canada standards</li>
                <li>U.S. CBP requirements</li>
                <li>Preparation fees disclosed prior</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
